import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { LS_KEY_THEME_MODE } from 'src/constants/localStorageKeys';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import lightTheme, { darkTheme } from '.';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeModeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  resolvedMode: 'light' | 'dark';
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  setMode: () => {},
  resolvedMode: 'light',
});

export const useThemeMode = () => useContext(ThemeModeContext);

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider = ({ children }: AppThemeProviderProps) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(LS_KEY_THEME_MODE);
    return (stored as ThemeMode) || 'light';
  });

  // Re-renders when OS preference changes; only used when mode === 'system'.
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true,
  });

  const setMode = (newMode: ThemeMode) => {
    localStorage.setItem(LS_KEY_THEME_MODE, newMode);
    setModeState(newMode);
  };

  const resolvedMode: 'light' | 'dark' = useMemo(() => {
    if (mode === 'dark') return 'dark';
    if (mode === 'light') return 'light';
    return prefersDark ? 'dark' : 'light';
  }, [mode, prefersDark]);

  const resolvedTheme = resolvedMode === 'dark' ? darkTheme : lightTheme;

  // Stamp data-theme on <html> so plain CSS (scrollbars, body bg) can react
  // to the resolved mode without needing MUI theme access.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedMode);
  }, [resolvedMode]);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode, resolvedMode }}>
      <ThemeProvider theme={resolvedTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
